import { Link } from "react-router-dom";
import ScrollReveal from "../components/marketing/ScrollReveal";
import { useLang } from "../contexts/LanguageContext";
import PageHead from "../components/PageHead";

export default function EngagementsPage() {
  const { t } = useLang();
  return (
    <div data-testid="engagements-page">
      <PageHead title="Engagements" description="Anti-gaspillage, securite alimentaire, transparence : les trois engagements non negociables de KYRA." />
      {/* PAGE HERO */}
      <div className="relative overflow-hidden" style={{ padding: "130px 5.5% 72px", background: "var(--kyra-bg)", borderBottom: "1px solid var(--kyra-li)" }}>
        <div className="absolute pointer-events-none select-none" style={{ right: "-2%", bottom: "-15%", fontFamily: "'Playfair Display'", fontSize: "min(32vw,440px)", fontWeight: 900, color: "rgba(34,197,94,0.04)", lineHeight: 0.8, letterSpacing: -8 }}>ENG</div>
        <ScrollReveal>
          <div className="flex gap-1.5 flex-wrap mb-6">
            {[t("engagements.kw1"), t("engagements.kw2"), t("engagements.kw3")].map((kw, i) => (
              <span key={i} className="text-[10px] font-medium uppercase tracking-[3px] px-2.5 py-1 rounded-sm" style={{ border: `1px solid ${i === 0 ? "rgba(27,67,50,0.25)" : i === 1 ? "rgba(176,48,37,0.25)" : "var(--kyra-li)"}`, color: i === 0 ? "var(--kyra-gr)" : i === 1 ? "var(--kyra-rd)" : "var(--kyra-mu)" }}>{kw}</span>
            ))}
          </div>
        </ScrollReveal>
        <ScrollReveal><h1 className="font-black leading-[1.07] mb-5 max-w-[680px]" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(34px,4.5vw,58px)", letterSpacing: -2 }}>{t("engagements.h1_1")}<br /><em style={{ color: "var(--kyra-gr)" }}>{t("engagements.h1_2")}</em></h1></ScrollReveal>
        <ScrollReveal delay="d1"><p className="text-base font-light leading-[1.78] max-w-[540px]" style={{ color: "var(--kyra-mu)" }}>{t("engagements.sub")}</p></ScrollReveal>
      </div>

      {/* PILIERS */}
      <section style={{ padding: "88px 5.5%" }}>
        <div className="flex flex-col">
          {[
            { n: "01", tag: t("engagements.p1Tag"), cls: "g", title: t("engagements.p1Title"), p1: t("engagements.p1T1"), p2: t("engagements.p1T2") },
            { n: "02", tag: t("engagements.p2Tag"), cls: "r", title: t("engagements.p2Title"), p1: t("engagements.p2T1"), p2: t("engagements.p2T2") },
            { n: "03", tag: t("engagements.p3Tag"), cls: "a", title: t("engagements.p3Title"), p1: t("engagements.p3T1"), p2: t("engagements.p3T2") },
          ].map((p, i) => {
            const colors = { g: "var(--kyra-gr)", r: "var(--kyra-rd)", a: "var(--kyra-am)" };
            return (
              <ScrollReveal key={i}>
                <div className="grid grid-cols-1 sm:grid-cols-[80px_1fr] gap-0 items-start" style={{ borderTop: "1px solid var(--kyra-li)", padding: "56px 0", borderBottom: i === 2 ? "1px solid var(--kyra-li)" : "none" }}>
                  <div className="font-black text-5xl leading-none tracking-tight opacity-[0.12]" style={{ fontFamily: "'Playfair Display'", letterSpacing: -2, color: colors[p.cls] }}>{p.n}</div>
                  <div>
                    <span className="text-[9px] tracking-[4px] uppercase font-medium block mb-3.5" style={{ color: colors[p.cls] }}>{p.tag}</span>
                    <h3 className="font-black leading-[1.15] tracking-tight mb-4" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(22px,2.5vw,30px)", letterSpacing: -0.5 }}>{p.title}</h3>
                    <p className="text-[14.5px] font-light leading-[1.82] max-w-[620px]" style={{ color: "var(--kyra-mu)" }}>{p.p1}</p>
                    <p className="text-[14.5px] font-light leading-[1.82] max-w-[620px] mt-3.5" style={{ color: "var(--kyra-mu)" }}>{p.p2}</p>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </section>

      {/* VALEURS */}
      <section style={{ background: "var(--kyra-bg2)", padding: "88px 5.5%" }}>
        <div className="mb-12">
          <ScrollReveal><div className="flex items-center gap-2.5 mb-4 text-[10px] font-medium uppercase tracking-[4px]" style={{ color: "var(--kyra-gr)" }}><span className="w-5 h-px" style={{ background: "var(--kyra-gr)" }} /> {t("engagements.valLabel")}</div></ScrollReveal>
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px,3.8vw,46px)", letterSpacing: -1.5 }}>{t("engagements.valTitle")}</h2></ScrollReveal>
        </div>
        <ScrollReveal delay="d1">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "var(--kyra-li)" }}>
            {[
              { title: t("engagements.v1"), desc: t("engagements.v1D") },
              { title: t("engagements.v2"), desc: t("engagements.v2D") },
              { title: t("engagements.v3"), desc: t("engagements.v3D") },
              { title: t("engagements.v4"), desc: t("engagements.v4D") },
              { title: t("engagements.v5"), desc: t("engagements.v5D") },
              { title: t("engagements.v6"), desc: t("engagements.v6D") },
            ].map((v, i) => (
              <div key={i} className="p-7 md:p-8" style={{ background: "var(--kyra-bg2)" }}>
                <h4 className="text-base font-black mb-2 tracking-tight" style={{ fontFamily: "'Playfair Display'" }}>{v.title}</h4>
                <p className="text-[12.5px] font-light leading-relaxed" style={{ color: "var(--kyra-mu)" }}>{v.desc}</p>
              </div>
            ))}
          </div>
        </ScrollReveal>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden text-center" style={{ background: "var(--kyra-dk)", color: "#fff", padding: "88px 5.5%" }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[560px] h-[560px] pointer-events-none" style={{ background: "radial-gradient(ellipse, rgba(27,67,50,0.2) 0%, transparent 70%)" }} />
        <div className="relative z-10 max-w-[560px] mx-auto">
          <ScrollReveal><h2 className="font-black leading-[1.1] mb-3 text-white" style={{ fontFamily: "'Playfair Display'", fontSize: "clamp(28px,3.8vw,46px)" }}>{t("engagements.ctaTitle")}</h2></ScrollReveal>
          <ScrollReveal delay="d1"><p className="text-[15px] font-light max-w-[400px] mx-auto mb-8" style={{ color: "rgba(255,255,255,0.35)" }}>{t("engagements.ctaSub")}</p></ScrollReveal>
          <ScrollReveal delay="d2"><Link to="/contact" className="no-underline bg-white px-7 py-3.5 rounded-sm text-[13px] font-medium transition-opacity hover:opacity-90 inline-block" style={{ color: "var(--kyra-dk)" }}>{t("engagements.ctaBtn")}</Link></ScrollReveal>
        </div>
      </section>
    </div>
  );
}
